import { load } from '@loaders.gl/core';
import { BitmapLayer } from '@deck.gl/layers';
import { TileLayer } from '@deck.gl/geo-layers';
import BaseTileLayer from '@arcgis/core/layers/BaseTileLayer';
import request from '@arcgis/core/request';
import type { Layer as FulcrumLayer } from '../../../models/layer';
import type { Projection } from '../projections';
import { Projections } from '../projections';

// Returns only origin+pathname to avoid leaking sensitive query params (e.g. tokens) into logs.
function redactUrl(urlString: string): string {
  try {
    const { origin, pathname } = new URL(urlString);
    return `${origin}${pathname}`;
  } catch {
    return '[invalid URL]';
  }
}

export interface WmsBbox {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
}

interface TileProps {
  tile: {
    bbox: {
      west: number;
      south: number;
      east: number;
      north: number;
    };
  };
  data: unknown;
  [key: string]: unknown;
}

const SUPPORTED_PROJECTIONS = {
  'EPSG:3857': Projections['EPSG:3857'],
  'EPSG:102100': Projections['EPSG:102100'],
};

const DEFAULT_PROJECTION = Projections['EPSG:3857'];

// Params that will always be set on a GetMap request
const STATIC_GETMAP_PARAMS = {
  request: 'GetMap',
  service: 'WMS',
  format: 'image/png',
  width: '256',
  height: '256',
};

// Default values for params on a GetMap request
const DEFAULT_GETMAP_PARAMS = {
  // Different server providers have varying support how to provide the projection.
  // We just provide both srs and crs to have better interoperability.
  srs: DEFAULT_PROJECTION.name,
  crs: DEFAULT_PROJECTION.name,
  styles: '',
  bgcolor: '0xFFFFFF',
  version: '1.1.1',
  transparent: 'true',
  outline: 'false',
};

export function convertWmsLayerToMapLayer(fulcrumLayer: FulcrumLayer): TileLayer {
  try {
    const validatedUrl = new URL(fulcrumLayer.source);
    if (!validatedUrl.origin) throw new Error('Invalid origin');
  } catch (err) {
    throw new Error(`Invalid source for layer: ${fulcrumLayer.name}`);
  }

  return new TileLayer({
    id: fulcrumLayer.id,
    getTileData: (tile) => {
      try {
        // @ts-ignore
        return load(createWmsGetMapUrl(fulcrumLayer.source, tile.x, tile.y, tile.z), null);
      } catch (error) {
        console.error(error);
        return null;
      }
    },
    minZoom: 0,
    maxZoom: 22,
    tileSize: 256,
    updateTriggers: {
      getTileData: [fulcrumLayer],
    },
    renderSubLayers: (props: TileProps) => {
      const {
        west, south, east, north,
      } = props.tile.bbox;

      return new BitmapLayer(props, {
        data: null,
        image: props.data,
        bounds: [west, south, east, north],
      });
    },
  });
}

export function createWmsGetMapUrl(urlString: string, x: number, y: number, z: number): string {
  let url: URL;
  try {
    url = new URL(urlString);
  } catch (error) {
    throw new Error(`Invalid WMS url: ${urlString}`);
  }

  const layerSourceParams: Record<string, string> = {};
  url.searchParams.forEach((value: string, key: string) => {
    layerSourceParams[key.toLowerCase()] = value;
  });

  const wmsParams = new URLSearchParams({
    ...DEFAULT_GETMAP_PARAMS,
    ...layerSourceParams,
    ...STATIC_GETMAP_PARAMS,
  });

  // Make sure srs and crs are set to the same value, prioritizing the value set in the layer source
  if (wmsParams.get('srs') !== wmsParams.get('crs')) {
    if (layerSourceParams.srs) {
      wmsParams.set('crs', layerSourceParams.srs);
    } else if (layerSourceParams.crs) {
      wmsParams.set('srs', layerSourceParams.crs);
    }
  }

  // Use a supported projection when the provided value is not supported
  // @ts-ignore
  if (!SUPPORTED_PROJECTIONS[wmsParams.get('srs')]) {
    wmsParams.set('crs', DEFAULT_PROJECTION.name);
    wmsParams.set('srs', DEFAULT_PROJECTION.name);
  }
  // @ts-ignore
  const projection = SUPPORTED_PROJECTIONS[wmsParams.get('srs')];
  const bbox = tileCoordsToWmsBbox(x, y, z, projection);
  wmsParams.set('bbox', [bbox.minX, bbox.minY, bbox.maxX, bbox.maxY].join(','));

  return `${url.origin}${url.pathname}?${wmsParams}`;
}

// Convert xyz tile coordinates to mercator bounds
export function tileCoordsToWmsBbox(x: number, y: number, z: number, projection: Projection): WmsBbox {
  const originX = projection.bounds.minX;
  const originY = projection.bounds.maxY;
  const tileSize = (-originX * 2) / (2 ** z);
  const minX = originX + (x * tileSize);
  const maxX = originX + ((x + 1) * tileSize);
  const minY = originY - ((y + 1) * tileSize);
  const maxY = originY - (y * tileSize);
  return {
    minX, minY, maxX, maxY,
  };
}

// @ts-ignore createSubclass exists at runtime but is not in the type declarations
export const WMSBaseTileLayer = BaseTileLayer.createSubclass({
  properties: {
    wmsUrl: null,
  },

  getTileUrl(level, row, col) {
    try {
      return createWmsGetMapUrl(this.wmsUrl, col, row, level);
    } catch {
      // Suppress per-tile errors to avoid noisy repeated logs; fetchTile rejects with a safe message.
      return '';
    }
  },

  fetchTile(level, row, col, options) {
    const url = this.getTileUrl(level, row, col);
    if (!url) {
      return Promise.reject(new Error('Cannot fetch WMS tile without a valid URL'));
    }

    return request(url, {
      responseType: 'image',
      signal: options?.signal,
    }).then((response) => response.data);
  },
});

export function convertWMSLayerToArcGISLayer(layer) {
  const url = layer.url ?? '';

  try {
    const parsed = new URL(url);
    if (!parsed.origin) throw new Error('Invalid origin');
  } catch {
    console.warn('Invalid WMS layer URL:', redactUrl(url));
    return new WMSBaseTileLayer({ id: layer.id, wmsUrl: '' });
  }

  return new WMSBaseTileLayer({
    id: layer.id,
    wmsUrl: url,
  });
}
